import { Text, Stack, useDisclosure, Flex } from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue, Cell } from 'react-table';

import Button from 'components/Button';
import { ConfirmationModal } from 'components/Modal';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import Table from 'components/Table';
import Title from 'components/Title';
import { fetchPaginatedPaychexApiUsers } from 'hooks/integrations/paychexApi/useFetchPaginatedPaychexApiUsers';
import {
  useFetchPaginatedPaychexApiWorkers,
  IPaginatedPaychexApiWorkersSearchQuery,
} from 'hooks/integrations/paychexApi/useFetchPaginatedPaychexApiWorkers';
import { IMapPaychexApiData, useMapPaychexApiWorkers } from 'hooks/integrations/paychexApi/useMapPaychexApiWorkers';
import {
  IRemovePaychexApiWorker,
  useRemovePaychexApiWorkerMappings,
} from 'hooks/integrations/paychexApi/useRemovePaychexApiWorkerMapping';
import { useSyncPaychexApi } from 'hooks/integrations/paychexApi/useSyncPaychexApi';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

const Mapping: React.FC<unknown> = () => {
  const [userValues, setUserValues] = useState<any>(null);
  const [enableSyncPaychexApi, setEnableSyncPaychexApi] = useState<boolean>(false);
  const [selectedEntity, setSelectedEntity] = useState<any>({
    timeero_user_id: null,
    worker_id: null,
  });
  const [searchQuery, setSearchQuery] = useState<IPaginatedPaychexApiWorkersSearchQuery>({
    page: 1,
    page_size: 25,
  });

  const {
    isOpen: isMapConfirmationModalOpen,
    onOpen: onMapConfirmationModalOpen,
    onClose: onMapConfirmationModalClose,
  } = useDisclosure();
  const {
    isOpen: isRemoveMappingConfirmationModalOpen,
    onOpen: onRemoveMappingConfirmationModalOpen,
    onClose: onRemoveMappingConfirmationModalClose,
  } = useDisclosure();

  const { t } = useTranslation(['common', 'integrations', 'employees', 'jobsTasks']);

  const { data, status, refetch } = useFetchPaginatedPaychexApiWorkers(searchQuery);
  const { status: mapPaychexApiWorkerStatus, mutateAsync: mapPaychexApiWorkerMutateAsync } = useMapPaychexApiWorkers();
  const { status: removePaychexApiWorkerMappingStatus, mutateAsync: removePaychexApiMappingMutateAsync } =
    useRemovePaychexApiWorkerMappings();
  const { isFetching: isSyncPaychexApiFetching } = useSyncPaychexApi({
    enabled: enableSyncPaychexApi,
    onSettled: (data: any) => {
      setEnableSyncPaychexApi(false);
      if (data) {
        refetch();
      }
    },
  });

  const columns = [
    {
      Header: t('common:name'),
      accessor: 'name',
      Cell: (data: CellValue) => (
        <Stack direction="column" spacing="0.5">
          <Text fontWeight="semibold" color="primary.500">
            {data.value || ''}
          </Text>
        </Stack>
      ),
    },
    {
      Header: t('integrations:workerType'),
      accessor: 'worker_type',
      width: 240,
      Cell: (data: CellValue) => <Text>{data.value || '- '}</Text>,
    },

    {
      Header: t('integrations:timeeroUser'),
      accessor: 'timeero_user',
      width: 280,
      Cell: (data: CellValue) => {
        return (
          <ReactSelectAsyncPaginate
            loadOptions={(search: any, _: any, { page }: any) => userLoadOptions(page, search)}
            additional={{
              page: 1,
            }}
            value={
              data.value
                ? data.value
                : userValues && userValues[`${data.row.original.id}`]
                  ? userValues[`${data.row.original.id}`]
                  : null
            }
            onChange={(value: any) => {
              setUserValues((prevValue: any) => ({
                ...prevValue,
                [data.row.original.id]: value || null,
              }));
            }}
            size="md"
            variant="filled"
            fontSize="sm"
            debounceTimeout={300}
            menuPortalTarget={document.body}
            isSearchable
            placeholder={t('integrations:selectTimeeroUser')}
            getOptionLabel={(option: any) => `${option.firstName} - ${option.lastName}`}
            getOptionValue={(option: any) => option.id}
            isDisabled={data.value}
          />
        );
      },
    },

    {
      Header: '',
      accessor: 'action',
      width: 180,
      Cell: (data: Cell<any>) => {
        return data.row.original.timeero_user ? (
          <Button
            bg="rgba(238, 112, 56, 0.1)"
            _hover={{ bg: 'rgba(238, 112, 56, 0.2)' }}
            _active={{ bg: 'rgba(238, 112, 56, 0.2)' }}
            color="secondary.300"
            onClick={() => handleRemoveWorkers(data.row.original)}
          >
            {t('integrations:removeEmployee')}
          </Button>
        ) : (
          <Button
            bg="rgba(238, 112, 56, 0.1)"
            _hover={{ bg: 'rgba(238, 112, 56, 0.2)' }}
            _active={{ bg: 'rgba(238, 112, 56, 0.2)' }}
            color="secondary.300"
            onClick={() => handleMapWorkers(data.row.original)}
          >
            {t('integrations:mapWorker')}
          </Button>
        );
      },
    },
  ];

  const userLoadOptions = (page: number, search: string) => {
    return getAsyncSelectLoadOptions(fetchPaginatedPaychexApiUsers, { page, search });
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IPaginatedPaychexApiWorkersSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handleMapWorkers = (selectedRow: any) => {
    const userObj = userValues ? userValues[`${selectedRow?.id}`] : undefined;

    setSelectedEntity({
      timeero_user_id: selectedRow?.timeero_user?.id ? selectedRow?.timeero_user?.id : userObj?.id || null,
      worker_id: selectedRow.id,
    });
    onMapConfirmationModalOpen();
  };

  const handleRemoveWorkers = (selectedRow: any) => {
    const userObj = userValues ? userValues[`${selectedRow?.id}`] : undefined;

    setSelectedEntity({
      timeero_user_id: selectedRow?.timeero_user?.id ? selectedRow?.timeero_user?.id : userObj?.id || null,
      worker_id: selectedRow.id,
    });
    onRemoveMappingConfirmationModalOpen();
  };

  const mapWorkers = () => {
    const postData: IMapPaychexApiData = selectedEntity;
    mapPaychexApiWorkerMutateAsync(postData).then(() => {
      onMapConfirmationModalClose();
    });
  };

  const removeWorkersMapping = () => {
    const postData: IRemovePaychexApiWorker = { worker_id: selectedEntity.worker_id };
    removePaychexApiMappingMutateAsync(postData).then(() => {
      onRemoveMappingConfirmationModalClose();
    });
  };

  const handleSyncPaychexApi = () => {
    setEnableSyncPaychexApi(true);
  };

  return (
    <>
      <ConfirmationModal
        isOpen={isMapConfirmationModalOpen}
        onClose={onMapConfirmationModalClose}
        title={t('integrations:mapWorkerToTimeero')}
        subTitle={t('integrations:mapWorkerToTimeeroSubTitle')}
        confirmButtonLabel={t('integrations:mapWorker')}
        confirmButtonProps={{
          bg: 'rgba(38, 103, 201, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(38, 103, 201, 0.2)',
          },
          _active: {
            bg: 'rgba(38, 103, 201, 0.2)',
          },
          isLoading: mapPaychexApiWorkerStatus === 'loading',
          onClick: mapWorkers,
        }}
      />

      <ConfirmationModal
        isOpen={isRemoveMappingConfirmationModalOpen}
        onClose={onRemoveMappingConfirmationModalClose}
        title={t('integrations:removeMapping')}
        subTitle={t('integrations:confirmRemoveMappingWorker')}
        confirmButtonLabel={t('integrations:removeMapping')}
        confirmButtonProps={{
          bg: 'rgba(38, 103, 201, 0.1)',
          color: 'primary.500',
          _hover: {
            bg: 'rgba(38, 103, 201, 0.2)',
          },
          _active: {
            bg: 'rgba(38, 103, 201, 0.2)',
          },
          isLoading: removePaychexApiWorkerMappingStatus === 'loading',
          onClick: removeWorkersMapping,
        }}
      />

      <Title>{t('integrations:paychexApi.paychexWorkerApiMapping')}</Title>
      <Flex justifyContent="flex-end">
        <Button
          colorScheme="secondary"
          mt={{ base: '5', md: '0' }}
          isLoading={isSyncPaychexApiFetching}
          onClick={handleSyncPaychexApi}
        >
          {t('integrations:paychexApi.syncChanges')}
        </Button>
      </Flex>
      <Table
        columns={columns}
        data={data?.payload || []}
        paginationData={data?.meta_data?.pagination}
        isLoading={status === 'loading'}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default Mapping;
